import { DraggableList } from 'components/common';
import { ViewPanelOption } from 'components/core';
import { useContext, useState } from 'react';
import { FilterContext, ViewContext } from 'utils/context';
import { usePermissions } from 'utils/hooks';

import {
  Box,
  Flex,
  LocalNavigation,
  LocalNavigationItem,
  Typography,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

const styles = {
  option: {
    padding: '0 0 0 $medium',
    '&:hover': { bgColor: 'rgba(255,255,255,0.2)' },
  },
  text: {
    flex: 1,
    color: '$text-contrast',
    cursor: 'pointer',
    overflow: 'hidden',
    padding: '$medium 0',
    textOverflow: 'ellipsis',
    textWrap: 'nowrap',
  },
};

function ViewPanel() {
  const { savedOrders, savedShipments, viewType } = useContext(ViewContext);
  const { filters, setFilters, setFiltersKey } = useContext(FilterContext);
  const { t } = useTranslation();
  const {
    permissions: { canViewOrders },
  } = usePermissions();
  const [tab, setTab] = useState(viewType);
  const isOrder = tab === 'order' && canViewOrders;

  const handleView = (listView: string, viewType: string) =>
    setFilters({ listView, viewType });

  const isAllSelected = ['all-orders', 'all-shipments'].includes(
    filters.listView as string,
  );
  const savedEntities = isOrder ? savedOrders : savedShipments;
  const fallbackViewId = isOrder ? 'all-orders' : 'all-shipments';
  const handleEntity = isOrder
    ? () => handleView('all-orders', 'order')
    : () => handleView('all-shipments', 'shipment');

  return (
    <Flex orientation="vertical" css={{ height: '100%' }}>
      <LocalNavigation css={{ padding: '$large $medium' }}>
        <LocalNavigationItem
          aria-label={t('core.shipments')}
          children={t('core.shipments')}
          isSelected={tab === 'shipment'}
          onPress={() => {
            setTab('shipment');
            setFilters({ listView: 'all-shipments', viewType: 'shipment' });
          }}
        />
        {canViewOrders && (
          <LocalNavigationItem
            aria-label={t('core.orders')}
            children={t('core.orders')}
            isSelected={tab === 'order'}
            onPress={() => {
              setTab('order');
              setFilters({ listView: 'all-orders', viewType: 'order' });
            }}
          />
        )}
      </LocalNavigation>
      <Flex css={{ ...styles.option, bgColor: isAllSelected ? '#415799' : '' }}>
        <Typography
          children={isOrder ? t('core.allOrders') : t('core.allShipments')}
          onClick={handleEntity}
          variant={isAllSelected ? 'subtextBold' : 'subtext'}
          css={styles.text}
        />
      </Flex>
      <Box css={{ overflow: 'scroll', paddingBottom: '$large', flex: 1 }}>
        <DraggableList
          onChange={savedEntities.sortViews}
          list={savedEntities.data.views.map((view) => ({
            id: view?.id || '',
            Component: (
              <ViewPanelOption
                view={view}
                savedEntities={savedEntities}
                handleView={() => handleView(view?.id || '', tab)}
                setFiltersKey={setFiltersKey}
                isSelected={view?.id === filters.listView}
                fallbackViewId={fallbackViewId}
              />
            ),
          }))}
        />
      </Box>
    </Flex>
  );
}

export default ViewPanel;
